import React, { useState } from "react";
import { Box, TextField, Button, Typography, Paper, Popover, CircularProgress, Alert } from "@mui/material";
import axios from "axios";

interface LoginDropdownProps {
  onLogin: (token: string, userId: string, username: string) => void;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const LoginDropdown: React.FC<LoginDropdownProps> = ({ onLogin, anchorEl, onClose }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const open = Boolean(anchorEl);
  const id = open ? "login-popover" : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${backendUrl}/api/login`, { email, password });
      if (response.data && response.data.token) {
        onLogin(response.data.token, response.data.userId, response.data.username);
        onClose();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to login. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <Paper sx={{ p: 3, width: 300 }}>
        <Typography component="h2" variant="h6" align="center" gutterBottom>
          Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "Login"}
          </Button>
        </Box>
      </Paper>
    </Popover>
  );
};

export default LoginDropdown;